import React, {useMemo, useState} from 'react'
import {Button, Checkbox, FormControlLabel, TextField} from '@mui/material'
import {Link} from 'react-router-dom'
import {useCurrency} from '../../../../hooks/useCurrency'
import {moneyFromMinor, moneyToMinor, rateFromScaled, rateToScaled} from '../../../utils/UnitConversion.utils'
import api from '../../../../api/axios'
import {TransactionDetailsDTO} from '../DepositWithdrawModal'
import KycConfirmation from './KycConfirmation'

type CheckoutStepProps = {
  provider: 'card' | 'bank' | null
  amountInputInUserCurrency: string
  targetAmountInUsd: number
  setModalOpen: (open: boolean) => void
  kyc: string
  currency: string
  currencySymbol: string
  currencyRate: number
  eurRateScaled: number
  transactionDetails: TransactionDetailsDTO | undefined
  minAmount?: number
  maxAmount?: number
}

type SpeedOption = 'default' | 'priority' | 'instant'

const validateIban = (iban: string): boolean => {
  const cleaned = iban.replace(/\s+/g, '').toUpperCase()
  if (!/^[A-Z0-9]+$/.test(cleaned)) return false
  if (cleaned.length < 15 || cleaned.length > 34) return false

  const rearranged = cleaned.slice(4) + cleaned.slice(0, 4)
  const numeric = rearranged
  .split('')
  .map(c => (c >= 'A' && c <= 'Z' ? (c.charCodeAt(0) - 55).toString() : c))
  .join('')

  let remainder = 0
  for (let i = 0; i < numeric.length; i += 7) {
    const block = remainder + numeric.substring(i, i + 7)
    remainder = Number(block) % 97
  }

  return remainder === 1
}

const CheckoutStep: React.FC<CheckoutStepProps> = ({
                                                     provider,
                                                     amountInputInUserCurrency,
                                                     targetAmountInUsd,
                                                     setModalOpen,
                                                     kyc,
                                                     currency,
                                                     currencySymbol,
                                                     currencyRate,
                                                     eurRateScaled,
                                                     transactionDetails,
                                                     minAmount,
                                                     maxAmount,
                                                   }) => {
  const {formatAmountWithoutCurrency, convertFromUSD} = useCurrency()
  const [agreeTerms, setAgreeTerms] = useState(false)
  const [speed, setSpeed] = useState<SpeedOption>('default')
  const [iban, setIban] = useState(transactionDetails?.iban || '')
  const [saveIban, setSaveIban] = useState(false)

  const kycRequired = provider === 'bank' && kyc !== 'APPROVED'

  const isIbanValid = useMemo(() => {
    if (provider !== 'bank') return true
    if (!iban) return false
    return validateIban(iban)
  }, [iban, provider])

  let feeType: string | null = null
  let providerFeeFixed = 0
  let providerFeePercent = 0

  switch (provider) {
    case 'bank':
      feeType = 'OPEN_BANKING'
      break
    case 'card':
      feeType = currency === 'EUR' ? 'CREDIT_CARD_EUR' : 'CREDIT_CARD_GLOBAL'
      break
    default:
      feeType = null
  }

  if (feeType && transactionDetails) {
    const fee = transactionDetails.withdrawProviderFees.find(f => f.feeType === feeType)
    if (fee) {
      providerFeeFixed = Number(
          feeType === 'CREDIT_CARD_GLOBAL'
              ? convertFromUSD(moneyFromMinor(fee.fixedMinor))
              : moneyFromMinor(fee.fixedMinor),
      ) || 0
      providerFeePercent = rateFromScaled(fee.rateScaled) || 0
    }
  }

  const usdAmount = Number.parseFloat(amountInputInUserCurrency.replace(',', '.')) || 0
  const displayedAmount =
      provider === 'bank'
          ? (currency === 'EUR' ? usdAmount : usdAmount * rateFromScaled(eurRateScaled))
          : usdAmount

  const speedFees: Record<SpeedOption, number> = {
    default: 0,
    priority: 0.01,
    instant: 0.03,
  }

  const speedTime: Record<SpeedOption, string> = {
    default: 'Within 24h',
    priority: 'Within 6h',
    instant: 'Within 15 Minutes',
  }

  const rawAvailability: Record<SpeedOption, string> = {
    default: '24/7',
    priority: '5:00 - 21:00',
    instant: '9:00 - 21:00',
  }

  const formatAvailability = (value: string) => {
    if (value === '24/7') return value
    const [start, end] = value.split(' - ')
    const today = new Date()
    const dateStr = today.toISOString().split('T')[0]
    const parseTime = (time: string) => {
      const [h, m] = time.split(':').map(Number)
      return {hour: h, minute: m}
    }
    const startTime = parseTime(start)
    const endTime = parseTime(end)
    const berlinToUser = (time: { hour: number; minute: number }) => {
      const formatter = new Intl.DateTimeFormat(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
      const berlinFormatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      })
      const berlinDate = new Date(`${dateStr}T${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}:00`)
      const parts = berlinFormatter.formatToParts(berlinDate)
      const map = Object.fromEntries(parts.map(p => [p.type, p.value]))
      const utcDate = new Date(`${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}:${map.second}Z`)
      return formatter.format(utcDate)
    }
    return `${berlinToUser(startTime)} - ${berlinToUser(endTime)}`
  }

  const speedAvailability: Record<SpeedOption, string> = {
    default: rawAvailability.default,
    priority: formatAvailability(rawAvailability.priority),
    instant: formatAvailability(rawAvailability.instant),
  }

  const fee = displayedAmount * providerFeePercent + providerFeeFixed + displayedAmount * speedFees[speed]
  const total = displayedAmount - fee

  const isAmountValid =
      targetAmountInUsd > 0 &&
      (minAmount === undefined || targetAmountInUsd >= minAmount) &&
      (maxAmount === undefined || targetAmountInUsd <= maxAmount)

  const formatDisplay = (value: number) => value.toFixed(2)

  return (
      <div className="flex flex-col p-4 border rounded-lg bg-gray-50 dark:bg-neutral-800">
        {kycRequired && (
            <div className="text-red-500 text-sm mb-2">
              You must complete{' '}
              <Link to="/profile/kyc" onClick={() => setModalOpen(false)} className="underline text-dark-primary">
                KYC-Verification
              </Link>.
            </div>
        )}

        <div className="flex justify-between text-sm">
          <div>Balance</div>
          <div>
            {provider === 'bank' ? (
                <>
                  {`($${formatAmountWithoutCurrency(targetAmountInUsd)}) `}
                  € {formatAmountWithoutCurrency(displayedAmount)}
                </>
            ) : (
                <>
                  {currency !== 'USD' && `($${formatAmountWithoutCurrency(targetAmountInUsd)}) `}
                  {currencySymbol} {formatAmountWithoutCurrency(displayedAmount)}
                </>
            )}
          </div>
        </div>

        <div className="flex justify-between text-sm mt-0.5">
          <div>Fee</div>
          <div className="flex gap-1">
            <div>{provider === 'bank' ? '€' : currencySymbol}</div>
            <div>{formatDisplay(fee)}</div>
          </div>
        </div>

        <div className="flex justify-between font-semibold mt-3">
          <div>Receive</div>
          <div className="flex gap-1">
            <div>{provider === 'bank' ? '€' : currencySymbol}</div>
            <div>{formatDisplay(total)}</div>
          </div>
        </div>

        <div className="mt-4">
          <div className="text-sm mb-2">Select withdrawal speed</div>
          <div className="flex gap-2">
            <Button variant={speed === 'default' ? 'contained' : 'outlined'} color="primary" className="flex-1 text-sm" onClick={() => setSpeed('default')}>
              Default 0%
            </Button>
            <Button variant={speed === 'priority' ? 'contained' : 'outlined'} color="primary" className="flex-1 text-sm" onClick={() => setSpeed('priority')}>
              Priority 1%
            </Button>
            <Button variant={speed === 'instant' ? 'contained' : 'outlined'} color="primary" className="flex-1 text-sm" onClick={() => setSpeed('instant')}>
              Instant 3%
            </Button>
          </div>
          <div className="text-xs mt-1 text-gray-500">
            Time: {speedTime[speed]} | Available: {speedAvailability[speed]}
          </div>
        </div>

        <div className={`transition-all duration-300 overflow-hidden ${provider === 'bank' ? 'max-h-50 pt-4' : 'max-h-0'}`}>
          <TextField
              label="IBAN"
              variant="outlined"
              size="small"
              fullWidth
              value={iban}
              onChange={e => setIban(e.target.value)}
              error={provider === 'bank' && !!iban && !isIbanValid}
              helperText={provider === 'bank' && !!iban && !isIbanValid ? 'Invalid IBAN' : ' '}
          />
          <FormControlLabel
              control={<Checkbox checked={saveIban} onChange={e => setSaveIban(e.target.checked)} color="primary" />}
              label={<div className="text-sm">Save IBAN for next time</div>}
          />
        </div>

        {kyc === 'APPROVED' && <KycConfirmation transactionDetails={transactionDetails} />}

        <FormControlLabel
            control={<Checkbox checked={agreeTerms} onChange={e => setAgreeTerms(e.target.checked)} color="primary" />}
            label={
              <div className="text-sm">
                I agree to the{' '}
                <Link to="/refund-policy" onClick={() => setModalOpen(false)} className="text-dark-primary underline">
                  Refund Policy
                </Link>{' '}
                and{' '}
                <Link to="/tos" onClick={() => setModalOpen(false)} className="text-dark-primary underline">
                  Terms of Service
                </Link>
              </div>
            }
        />

        <Button
            variant="contained"
            color="primary"
            className="mt-3 w-full"
            disabled={kycRequired || !agreeTerms || !isAmountValid || !provider || (provider === 'bank' && !isIbanValid)}
            onClick={async () => {
              if (!provider || !isAmountValid) return

              try {
                type WithdrawResponse = { redirectUrl: string }
                let response: { data: WithdrawResponse } | null = null

                if (provider === 'card') {
                  response = await api.post<WithdrawResponse>('/withdraw/stripe', {
                    amountUSD: targetAmountInUsd,
                  })
                } else if (provider === 'bank') {
                  if (usdAmount <= 0) {
                    alert('Please enter a valid amount')
                    return
                  }

                  if (!isIbanValid) {
                    alert('Invalid IBAN')
                    return
                  }

                  response = await api.post<WithdrawResponse>('/withdraw/open-banking', {
                    sourceAmountMinor: moneyToMinor(displayedAmount),
                    targetAmountMinor: moneyToMinor(targetAmountInUsd),
                    totalSourceAmountMinor: moneyToMinor(total),
                    sourceCurrency: 'EUR',
                    currencyRateScaled: rateToScaled(currencyRate),
                    iban: iban,
                    saveIban: saveIban,
                  })
                }

                const redirectUrl = response?.data?.redirectUrl
                if (redirectUrl) window.location.href = redirectUrl
                else alert('Failed to initiate withdraw. Please try again.')
              } catch (err) {
                console.error('StripeOnboarding failed', err)
                alert('Failed to initiate withdraw. Please try again.')
              }
            }}
        >
          Request Withdrawal
        </Button>

        <div className="text-xs text-gray-500 mt-1">
          This is a request. If we cannot fulfill it immediately, your funds will still be withdrawn. Excess fees will be refunded.
        </div>
      </div>
  )
}

export default CheckoutStep
